import { FileWarning, RefreshCw } from 'lucide-react';
import type { ReactElement } from 'react';

import { InlineError } from '@/components/ui/StatePanel';
import { StageResponsiblesCard } from '@/features/document-editor/components/StageResponsiblesCard';
import { useFlowCapabilities } from '@/features/document-editor/use-flow-capabilities';
import { errorAdvice, refreshAdvice } from '@/lib/error-advice';

// Ronda en curso en la ficha (G2-8): mientras el documento tiene una etapa de trabajo
// abierta, muestra sus responsables y quién falta. Sin etapa abierta no se muestra. Si la
// consulta falla no se sabe si hay ronda: la tarjeta lo dice y ofrece reintentar (UX-ERR-01).

export function DossierStageCard({
  entryId,
  companyId,
}: {
  entryId: string;
  companyId: string | null;
}): ReactElement | null {
  const caps = useFlowCapabilities(entryId, { companyId, enabled: companyId !== null });
  // Sin ronda a la vista (o sin datos), una falla impide saber si ahora hay una: se avisa.
  if (caps.isError && (caps.data?.stage ?? null) === null) {
    return (
      <section className="card rise d2" aria-labelledby="dossier-stage-title">
        <div className="ch">
          <h3 id="dossier-stage-title">Responsables de la etapa</h3>
        </div>
        <InlineError
          icon={<FileWarning />}
          onRetry={() => void caps.refetch()}
          retrying={caps.isFetching}
        >
          No se pudo cargar la ronda en curso del documento. {errorAdvice(caps.error)}
        </InlineError>
      </section>
    );
  }
  const stage = caps.data?.stage ?? null;
  if (stage === null) {
    return null;
  }
  const card = <StageResponsiblesCard stage={stage} actingAs={caps.data?.actingAs ?? null} />;
  if (!caps.isError) {
    return card;
  }
  // Una recarga que falla conserva la ronda que ya se veía y lo avisa.
  return (
    <>
      {card}
      <div className="card">
        <InlineError
          icon={<RefreshCw size={16} />}
          onRetry={() => void caps.refetch()}
          retrying={caps.isFetching}
        >
          No se pudo actualizar la ronda en curso. Se muestra la anterior.
          {refreshAdvice(caps.error)}
        </InlineError>
      </div>
    </>
  );
}
